import { packsOffered, type RemovalPlan, type TrackSummary } from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { EmptyState } from '../../components/EmptyState.js'
import { ExamplePackOffer } from '../../components/ExamplePackOffer.js'
import {
  HeaderLink,
  Icon,
  ICONS,
  Page,
  Section,
  SubHeader,
  TitleBlock,
} from '../../components/Layout.js'
import { useToast } from '../../components/Toast.js'
import { useServices } from '../../services/ServicesProvider.js'
import { RemoveTrackDialog } from './RemoveTrackDialog.js'
import { ArchivedTrackRow, TrackEditRow, TrackRow } from './TrackRow.js'
import styles from './TrackList.module.css'

/**
 * What the user measures (T-301).
 *
 * The removal path is the reason this screen exists in its own ticket: a track
 * with history archives rather than disappearing (ADR-0003), and the UI has to
 * make that intelligible rather than appearing to ignore the request.
 */
export function TrackListScreen() {
  const { storage, repos } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()

  const [summaries, setSummaries] = useState<TrackSummary[] | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [offered, setOffered] = useState(true)
  const [plan, setPlan] = useState<RemovalPlan | null>(null)
  const [editing, setEditing] = useState(false)

  const reload = useCallback(async () => {
    const [all, alreadyOffered] = await Promise.all([
      repos.tracks.summaries({ includeArchived: true }),
      packsOffered(storage),
    ])
    setSummaries(all)
    setOffered(alreadyOffered)
  }, [repos, storage])

  useEffect(() => {
    void reload()
  }, [reload])

  const active = (summaries ?? []).filter((s) => !s.track.archived)
  const archived = (summaries ?? []).filter((s) => s.track.archived)

  const move = useCallback(
    async (from: number, to: number) => {
      if (to < 0 || to >= active.length || from === to) {
        return
      }
      const order = active.map((s) => s.track.id)
      const [moved] = order.splice(from, 1)
      if (moved === undefined) {
        return
      }
      order.splice(to, 0, moved)
      // Archived tracks keep their positions and are appended, so reordering
      // the visible list cannot silently renumber things it is not showing.
      await repos.tracks.reorder([...order, ...archived.map((s) => s.track.id)])
      await reload()
    },
    [active, archived, repos, reload],
  )

  function planRemoval(trackId: string) {
    void repos.tracks.planRemoval(trackId).then((result) => {
      if (result.ok) {
        setPlan(result.value)
      }
    })
  }

  async function confirmRemoval() {
    if (!plan) {
      return
    }
    const { track } = plan
    setPlan(null)
    const result = await repos.tracks.remove(track.id)
    await reload()

    if (!result.ok) {
      show({ message: result.errors[0]?.message ?? 'That did not work.', tone: 'danger' })
      return
    }
    show({
      message:
        result.value.kind === 'delete'
          ? `Deleted ${track.name}.`
          : `Archived ${track.name}. Its history is kept.`,
    })
  }

  if (summaries === null) {
    return (
      <Page>
        <SubHeader back={{ to: '/settings', label: 'Settings' }} />
        <h1>Tracks</h1>
        <p>Loading…</p>
      </Page>
    )
  }

  const groups = groupSummaries(active)

  return (
    <Page>
      <SubHeader
        back={{ to: '/settings', label: 'Settings' }}
        action={
          <HeaderLink to="/tracks/new" icon={ICONS.plus}>
            New track
          </HeaderLink>
        }
      />
      <TitleBlock
        title="Tracks"
        lead="The things you measure. Each one keeps its own colour everywhere it appears."
      />

      {active.length === 0 ? (
        <EmptyState
          title="Nothing to measure yet"
          body={
            offered
              ? 'A track is anything you want to record over time — a 1–10 scale, a yes/no, a set of options. Create one to get started.'
              : 'A track is anything you want to record over time. Start from an example below, or create your own — the examples are ordinary tracks you can rename, change or delete.'
          }
          action={
            <ExamplePackOffer
              onInstalled={reload}
              onDeclined={() => {
                setOffered(true)
              }}
            />
          }
        />
      ) : editing ? (
        /*
          Edit mode is one flat list, not the groups. Order is global — it is
          the running order of a check-in (T-805) — and moving a track "up" past
          a group heading it does not belong to would be a move nobody can see.
        */
        <Section
          id="order"
          label="Order"
          aside={
            <button
              type="button"
              className={styles['modeButton']}
              aria-pressed={true}
              onClick={() => setEditing(false)}
            >
              Done
            </button>
          }
          note="Order sets the order you are asked."
        >
          {active.map((summary, index) => (
            <TrackEditRow
              key={summary.track.id}
              summary={summary}
              position={index}
              total={active.length}
              onMove={(from, to) => void move(from, to)}
              onArchive={() => {
                void repos.tracks.archive(summary.track.id).then(reload)
              }}
              onRemove={() => planRemoval(summary.track.id)}
            />
          ))}
        </Section>
      ) : (
        groups.map((group, index) => (
          <Section
            key={group.title ?? '(none)'}
            {...(group.title === null ? {} : { label: group.title })}
            aside={
              <>
                {group.tracks.length === 1
                  ? '1 track'
                  : `${String(group.tracks.length)} tracks`}
                {index === 0 && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className={styles['modeButton']}
                      aria-pressed={false}
                      onClick={() => setEditing(true)}
                    >
                      Edit list
                    </button>
                  </>
                )}
              </>
            }
          >
            {group.tracks.map((summary) => (
              <TrackRow key={summary.track.id} summary={summary} />
            ))}
          </Section>
        ))
      )}

      {archived.length > 0 && (
        <div className={styles['archived']} data-bleed>
          <button
            type="button"
            className={styles['disclosure']}
            aria-expanded={showArchived}
            onClick={() => setShowArchived((open) => !open)}
          >
            <span className={styles['disclosureLabel']}>Archived tracks</span>
            <span className={styles['disclosureDetail']}>
              {archived.length} · history kept
            </span>
            <Icon
              path={ICONS.chevronRight}
              className={`${styles['chevron']} ${showArchived ? styles['chevronOpen'] : ''}`}
            />
          </button>

          {showArchived &&
            archived.map((summary) => (
              <ArchivedTrackRow
                key={summary.track.id}
                summary={summary}
                onRestore={() => {
                  void repos.tracks.restore(summary.track.id).then(reload)
                }}
                onRemove={() => planRemoval(summary.track.id)}
              />
            ))}
        </div>
      )}

      <RemoveTrackDialog
        plan={plan}
        onCancel={() => setPlan(null)}
        onEdit={() => {
          const id = plan?.track.id
          setPlan(null)
          if (id !== undefined) {
            void navigate(`/tracks/${id}`)
          }
        }}
        onConfirm={() => void confirmRemoval()}
      />
    </Page>
  )
}

interface TrackGroup {
  /** `null` for tracks with no group, listed last under "Other". */
  readonly title: string | null
  readonly tracks: readonly TrackSummary[]
}

/**
 * The list's sections, from each track's optional group.
 *
 * Groups appear in the order of their first track, so the arrangement follows
 * the order the user set rather than the alphabet. Tracks with no group come
 * last, under "Other" — or with no heading at all when nothing is grouped,
 * where a lone "Other" would be a heading that names nothing.
 */
function groupSummaries(summaries: readonly TrackSummary[]): readonly TrackGroup[] {
  const byTitle = new Map<string, TrackSummary[]>()
  const ungrouped: TrackSummary[] = []
  for (const summary of summaries) {
    const group = summary.track.group?.trim()
    if (group === undefined || group === '') {
      ungrouped.push(summary)
      continue
    }
    const list = byTitle.get(group) ?? []
    list.push(summary)
    byTitle.set(group, list)
  }
  const groups: TrackGroup[] = [...byTitle].map(([title, tracks]) => ({ title, tracks }))
  if (ungrouped.length > 0) {
    groups.push({ title: groups.length === 0 ? null : 'Other', tracks: ungrouped })
  }
  return groups
}
